<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { parsePhoneNumber } from "awesome-phonenumber";
import OtpDigitInput from "./OtpDigitInput.vue";
import { RESEND_COOLDOWN_SECONDS } from "@/types/gate";
import { useCountdown } from "@/composables/useCountdown";

const props = defineProps<{
  validating: boolean;
  resending: boolean;
  whatsapp: string;
}>();

const emit = defineEmits<{
  submit: [code: string];
  resend: [];
  changeNumber: [];
}>();

const { t } = useI18n();
const { remaining, start } = useCountdown();
start(RESEND_COOLDOWN_SECONDS);

const code = ref("");
const digitInput = ref<InstanceType<typeof OtpDigitInput> | null>(null);

const formattedNumber = computed(() => {
  const parsed = parsePhoneNumber(props.whatsapp);
  return parsed.valid ? (parsed.number?.international ?? props.whatsapp) : props.whatsapp;
});

// Web OTP API — Chrome/Android reads the code from an SMS that ends with
// `@<origin> #<code>` and auto-fills the form with the user's consent.
// Feature-detected; silently no-ops on unsupported browsers (iOS relies on
// the input's autocomplete="one-time-code" attribute instead).
interface OtpCredential extends Credential {
  readonly code: string;
}
let otpAbortController: AbortController | null = null;

async function listenForSmsOtp(): Promise<void> {
  if (typeof window === "undefined" || !("OTPCredential" in window)) return;
  otpAbortController?.abort();
  otpAbortController = new AbortController();
  try {
    const credential = (await navigator.credentials.get({
      // `otp` isn't in the default CredentialRequestOptions type yet.
      otp: { transport: ["sms"] },
      signal: otpAbortController.signal,
    } as CredentialRequestOptions)) as OtpCredential | null;
    if (credential && typeof credential.code === "string" && credential.code.length > 0) {
      digitInput.value?.fill(credential.code);
    }
  } catch {
    // Aborted (navigation / unmount / resend) or browser rejected — fall back
    // to manual entry.
  }
}

function onComplete(value: string): void {
  code.value = value;
  if (!props.validating) {
    emit("submit", value);
  }
}

function onUpdate(value: string): void {
  code.value = value;
}

function onResend(): void {
  if (remaining.value > 0 || props.resending) return;
  emit("resend");
  start(RESEND_COOLDOWN_SECONDS);
  digitInput.value?.clear();
  void listenForSmsOtp();
}

function onSubmit(): void {
  if (code.value.length === 6 && !props.validating) {
    emit("submit", code.value);
  }
}

onMounted(() => {
  void listenForSmsOtp();
});

onBeforeUnmount(() => {
  otpAbortController?.abort();
  otpAbortController = null;
});

defineExpose({
  clear: () => digitInput.value?.clear(),
  focus: () => digitInput.value?.focus(),
});
</script>

<template>
  <form class="otp-form gate-card" novalidate @submit.prevent="onSubmit">
    <div class="otp-form__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
        <path
          d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8A2.5 2.5 0 0 1 17.5 17H9l-3.5 3V17A2.5 2.5 0 0 1 4 14.5v-8Z"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linejoin="round"
        />
        <path
          d="M8.5 10.5h7M8.5 7.5h4"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </div>

    <h2 class="gate-heading otp-form__heading">{{ t("otp.heading") }}</h2>
    <p class="otp-form__intro">
      {{ t("otp.intro") }}
      <strong class="otp-form__number" dir="ltr">{{ formattedNumber }}</strong>
    </p>
    <p class="otp-form__channels">{{ t("otp.channels") }}</p>

    <OtpDigitInput
      ref="digitInput"
      class="otp-form__digits"
      :disabled="validating"
      :autofocus="true"
      @update="onUpdate"
      @complete="onComplete"
    />

    <button
      type="submit"
      class="gate-button otp-form__submit"
      :disabled="code.length !== 6 || validating"
    >
      <span v-if="validating" class="gate-spinner" aria-hidden="true" />
      {{ validating ? t("otp.validating") : t("otp.submit") }}
    </button>

    <div class="otp-form__actions">
      <button
        type="button"
        class="gate-link"
        :disabled="remaining > 0 || resending"
        @click="onResend"
      >
        {{ remaining > 0 ? t("otp.resendIn", { seconds: remaining }) : t("otp.resend") }}
      </button>
      <button type="button" class="gate-link" @click="emit('changeNumber')">
        {{ t("otp.changeNumber") }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.otp-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  text-align: left;
}

.otp-form__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  margin-bottom: var(--space-2);
}

.otp-form__heading {
  margin-bottom: var(--space-1);
}

.otp-form__intro {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.otp-form__number {
  color: var(--color-text);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.otp-form__channels {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  margin-bottom: var(--space-2);
}

.otp-form__digits {
  margin-top: var(--space-2);
}

.otp-form__submit {
  margin-top: var(--space-3);
}

.otp-form__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-2);
}
</style>
